import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  LinearProgress,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  LocalHospital as DoctorIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface DaySchedule {
  id: number;
  dayOfWeek: string;
  dayName: string;
  dayNameHindi: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  slotDurationMinutes: number;
  breakStartTime: string | null;
  breakEndTime: string | null;
  maxAppointments: number;
  notes: string | null;
  workingHours: number;
}

interface WeeklySchedule {
  doctorId: number;
  doctorName: string;
  schedule: DaySchedule[];
  totalWorkingDays: number;
  totalWorkingHours: number;
}

interface Doctor {
  id: number;
  name: string;
  specialization: string;
  qualification: string;
  experience: number;
  department: string;
  status: string;
}

const DAYS_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

const getDayColor = (day: DaySchedule, index: number) => {
  if (!day.isAvailable) return '#f5f5f5';
  if (index < 5) return '#e8f5e9'; // Weekdays - green
  return '#fff3e0'; // Weekends - orange
};

const formatTime = (time: string | null) => {
  if (!time) return '--:--';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ padding: '24px 0' }}>
    {value === index && children}
  </div>
);

/**
 * Doctor Schedule Page
 * View a doctor's weekly schedule and availability
 */
const DoctorSchedulePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState<WeeklySchedule | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (id) {
      fetchDoctorSchedule();
      fetchDoctorDetails();
    }
  }, [id]);

  const fetchDoctorSchedule = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctor/schedule/doctor/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSchedule(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch doctor schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorDetails = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctors/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDoctor(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch doctor details:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 1 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Doctor Schedule
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mt: 0.5 }}>
            View doctor's weekly availability and time slots
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDoctorSchedule}
        >
          Refresh
        </Button>
      </Box>

      {/* Doctor Info Card */}
      {doctor && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'transparent' , fontSize: '1.5rem', fontWeight: 700 }}>
                {doctor.name?.charAt(0) || 'D'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {doctor.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip label={doctor.specialization} size="small" sx={{ bgcolor: 'transparent' , color: '#2563eb' }} />
                  <Chip label={doctor.department} size="small" sx={{ bgcolor: 'transparent' , color: '#16a34a' }} />
                  <Chip
                    label={doctor.status}
                    size="small"
                    sx={{
                      bgcolor: doctor.status === 'ACTIVE' ? '#dcfce7' : '#fee2e2',
                      color: doctor.status === 'ACTIVE' ? '#16a34a' : '#dc2626'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  {doctor.qualification} • {doctor.experience} years experience
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {schedule && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', mx: 'auto', mb: 1, border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                  {schedule.totalWorkingDays}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  Working Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', mx: 'auto', mb: 1, border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                  <TimeIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                  {schedule.totalWorkingHours}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  Hours/Week
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#B45309', mx: 'auto', mb: 1, border: '2px solid rgba(180, 83, 9, 0.2)' }}>
                  <ScheduleIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                  {schedule.schedule?.filter((d: DaySchedule) => d.isAvailable).length || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  Available Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#DC2626', mx: 'auto', mb: 1, border: '2px solid rgba(220, 38, 38, 0.2)' }}>
                  <CancelIcon />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                  {schedule.schedule?.filter((d: DaySchedule) => !d.isAvailable).length || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  Off Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: '1px solid #e2e8f0', px: 2 }}
        >
          <Tab icon={<WeekIcon />} label="Weekly Schedule" iconPosition="start" />
          <Tab icon={<TodayIcon />} label="Today's Availability" iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 1: Weekly Schedule */}
          <TabPanel value={activeTab} index={0}>
            {schedule && (
              <Grid container spacing={2}>
                {DAYS_ORDER.map((dayKey, index) => {
                  const day = schedule.schedule?.find((d: DaySchedule) => d.dayOfWeek === dayKey);
                  if (!day) return null;

                  return (
                    <Grid item xs={12} md={6} key={dayKey}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          bgcolor: getDayColor(day, index),
                          border: day.isAvailable ? '1px solid #e0e0e0' : '1px dashed #bdbdbd',
                          opacity: day.isAvailable ? 1 : 0.7,
                        }}
                      >
                        <CardContent>
                          {/* Day Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {day.dayName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {day.dayNameHindi}
                              </Typography>
                            </Box>
                            {day.isAvailable ? (
                              <Chip
                                icon={<CheckIcon />}
                                label={`${day.workingHours} hours`}
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<CancelIcon />}
                                label="Off"
                                color="default"
                                size="small"
                              />
                            )}
                          </Box>

                          {/* Time Details */}
                          {day.isAvailable && (
                            <Box>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Start Time
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {formatTime(day.startTime)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    End Time
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {formatTime(day.endTime)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Slot Duration
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {day.slotDurationMinutes} minutes
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Max Appointments
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {day.maxAppointments} / day
                                  </Typography>
                                </Grid>
                              </Grid>

                              {/* Break Time */}
                              {day.breakStartTime && day.breakEndTime && (
                                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'transparent' , borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600 }}>
                                    Break: {formatTime(day.breakStartTime)} - {formatTime(day.breakEndTime)}
                                  </Typography>
                                </Box>
                              )}

                              {/* Notes */}
                              {day.notes && (
                                <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
                                  Note: {day.notes}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>

          {/* Tab 2: Today's Availability */}
          <TabPanel value={activeTab} index={1}>
            {schedule && (() => {
              const today = new Date();
              const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
              const todayDayName = dayNames[today.getDay()];
              const todaySchedule = schedule.schedule?.find((d: DaySchedule) => d.dayOfWeek === todayDayName);

              if (!todaySchedule) {
                return (
                  <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ color: '#64748b' }}>
                      No schedule found for today
                    </Typography>
                  </Card>
                );
              }

              if (!todaySchedule.isAvailable) {
                return (
                  <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'transparent' }}>
                    <CancelIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#dc2626', mb: 1 }}>
                      Doctor is OFF Today
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                      Today is {todaySchedule.dayName} and the doctor is not available.
                    </Typography>
                  </Card>
                );
              }

              // Generate time slots for today
              const slots = [];
              let currentTime = todaySchedule.startTime;
              const endTime = todaySchedule.endTime;
              const slotDuration = todaySchedule.slotDurationMinutes || 30;
              const maxAppointments = todaySchedule.maxAppointments || 10;

              while (currentTime < endTime) {
                const [h, m] = currentTime.split(':').map(Number);
                const nextHour = Math.floor((h * 60 + m + slotDuration) / 60);
                const nextMin = (h * 60 + m + slotDuration) % 60;
                const nextTime = `${nextHour.toString().padStart(2, '0')}:${nextMin.toString().padStart(2, '0')}`;

                // Check if this slot is during break
                const isBreak = todaySchedule.breakStartTime && todaySchedule.breakEndTime &&
                  currentTime >= todaySchedule.breakStartTime && currentTime < todaySchedule.breakEndTime;

                slots.push({
                  startTime: currentTime,
                  endTime: nextTime,
                  available: !isBreak,
                  maxAppointments,
                  status: isBreak ? 'BREAK' : 'AVAILABLE'
                });

                currentTime = nextTime;
              }

              return (
                <Box>
                  {/* Today's Summary */}
                  <Card sx={{ mb: 3, borderRadius: 2, bgcolor: 'transparent' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'transparent' , width: 56, height: 56 }}>
                          <CheckIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: '#16a34a' }}>
                            Available Today
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#1e293b' }}>
                            {formatTime(todaySchedule.startTime)} - {formatTime(todaySchedule.endTime)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {slots.length}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Total Slots
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Time Slots Table */}
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Time Slot</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Max Appointments</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {slots.map((slot, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {/* HIGH CONTRAST: Light background + Dark text */}
                              <Chip
                                label={slot.status === 'BREAK' ? 'Break' : 'Available'}
                                size="small"
                                sx={{
                                  bgcolor: slot.status === 'BREAK' ? '#FEF3C7' : '#DCFCE7',
                                  color: slot.status === 'BREAK' ? '#B45309' : '#15803D',
                                  border: `1px solid ${slot.status === 'BREAK' ? 'rgba(180, 83, 9, 0.2)' : 'rgba(21, 128, 61, 0.2)'}`,
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell>{slot.maxAppointments}</TableCell>
                            <TableCell>{slotDuration} min</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Break Time Notice - HIGH CONTRAST */}
                  {todaySchedule.breakStartTime && todaySchedule.breakEndTime && (
                    <Card sx={{ mt: 2, p: 2, bgcolor: 'transparent' , borderRadius: 2, border: '1px solid rgba(180, 83, 9, 0.2)' }}>
                      <Typography variant="body2" sx={{ color: '#B45309', fontWeight: 500 }}>
                        <strong>Break Time:</strong> {formatTime(todaySchedule.breakStartTime)} - {formatTime(todaySchedule.breakEndTime)}
                      </Typography>
                    </Card>
                  )}
                </Box>
              );
            })()}
          </TabPanel>
        </Box>
      </Paper>

      {/* Quick Actions */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>Quick Actions</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PersonIcon />}
            onClick={() => navigate(`/doctors/${id}`)}
          >
            View Profile
          </Button>
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => navigate(`/appointments/book?doctorId=${id}`)}
          >
            Book Appointment
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default DoctorSchedulePage;
